import { saveFlow } from '../../api/flowsApi';
import { getOperationColor } from '../../utils/operationColors';
import { computeLevels } from './taskGraph';

const ROOT = {
  id: 'root',
  type: 'textNode',
  position: { x: 0, y: 0 },
  data: { title: 'Root', content: '', outputs: [], classificationLocks: {} },
  hidden: true,
};

const edge = (source, target, extra = {}) => ({
  id: `e${source}-${target}`,
  source,
  target,
  type: 'smoothstep',
  markerEnd: { type: 'arrowclosed' },
  ...extra,
});

/** Drop check steps, joining each one's in-edges to its out-edges. */
function bridgeChecks(graph) {
  let edges = graph.edges.map((e) => ({ ...e }));
  graph.steps.filter((s) => s.isCheck).forEach((c) => {
    const ins = edges.filter((e) => e.to === c.key && e.from !== c.key);
    const outs = edges.filter((e) => e.from === c.key && e.to !== c.key);
    edges = edges.filter((e) => e.from !== c.key && e.to !== c.key);
    ins.forEach((i) => outs.forEach((o) => {
      if (!edges.some((e) => e.from === i.from && e.to === o.to)) edges.push({ from: i.from, to: o.to });
    }));
  });
  return edges;
}

/** Saved graph must hold plain data only: loadGraphObject re-attaches callbacks. */
export function toCanvasGraph(graph, source) {
  const levels = computeLevels(graph);
  const byKey = Object.fromEntries(graph.steps.map((s) => [s.key, s]));
  const color = getOperationColor('create-prompt');
  const nodes = [{ ...ROOT, data: { ...ROOT.data, outputs: [], classificationLocks: {} } }];
  levels.forEach((level, levelIndex) => {
    level.filter((k) => !byKey[k].isCheck).forEach((k, i) => {
      const s = byKey[k];
      nodes.push({
        id: s.key,
        type: 'promptNode',
        position: { x: i * 300, y: (levelIndex + 1) * 200 },
        data: {
          title: s.title,
          content: s.prompt || s.summary || s.title,
          isPrompt: true,
          readOnly: true,
          isInput: false,
          isOutput: false,
          isLoading: false,
          tags: [],
          classificationLocks: {},
          handleColor: color,
        },
      });
    });
  });
  const bridged = bridgeChecks(graph);
  const hasIn = new Set(bridged.map((e) => e.to));
  const edges = [];
  nodes.slice(1).forEach((n) => {
    if (!hasIn.has(n.id)) edges.push(edge('root', n.id, { hidden: true }));
  });
  bridged.forEach((e) => edges.push(edge(e.from, e.to)));
  return { nodes, edges, viewport: { x: 0, y: 0, zoom: 1 }, source };
}

/** Saves a copy of the steps as a canvas flow and returns its id. */
export async function copyToCanvas({ name, graph, source }) {
  const saved = await saveFlow({
    graph: toCanvasGraph(graph, source),
    name: `${name} (copy)`,
    humanAuthored: true,
    autoSaved: false,
  });
  return saved.id;
}
